import { useState, useEffect, useCallback } from 'react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  Type, Image, Square, Minus, ArrowUpDown, List, Box, Users, FileText,
  GripVertical, Trash2, Plus, Eye, Send, Save, ChevronDown, ChevronRight,
  Palette, AlignLeft, AlignCenter, AlignRight, Bold, Italic, X, Check,
  Mail, Loader2, Copy, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Block types available for drag and drop
const BLOCK_TYPES = [
  { type: 'heading', label: 'Heading', icon: Type, description: 'Add a title or heading' },
  { type: 'text', label: 'Text', icon: FileText, description: 'Add paragraph text' },
  { type: 'button', label: 'Button', icon: Square, description: 'Add a call-to-action button' },
  { type: 'image', label: 'Image', icon: Image, description: 'Add an image' },
  { type: 'divider', label: 'Divider', icon: Minus, description: 'Add a horizontal line' },
  { type: 'spacer', label: 'Spacer', icon: ArrowUpDown, description: 'Add vertical space' },
  { type: 'list', label: 'List', icon: List, description: 'Add a bullet or numbered list' },
  { type: 'class_details', label: 'Class Details', icon: Box, description: 'Auto-generated class info block' },
  { type: 'students_list', label: 'Students List', icon: Users, description: 'Auto-generated students list' },
];

// Default content for each block type
const getDefaultBlockContent = (type) => {
  switch (type) {
    case 'heading':
      return { content: { text: 'Heading Text', level: 2 }, styles: { color: '#1e293b', textAlign: 'left' } };
    case 'text':
      return { content: { text: 'Enter your text here...' }, styles: { color: '#334155', textAlign: 'left', fontSize: '16px' } };
    case 'button':
      return { content: { text: 'Click Here', url: '{{payment_url}}' }, styles: { backgroundColor: '#0ea5e9', color: '#ffffff', textAlign: 'center' } };
    case 'image':
      return { content: { src: '', alt: '' }, styles: { width: '100%', textAlign: 'center' } };
    case 'divider':
      return { content: {}, styles: { borderColor: '#e2e8f0' } };
    case 'spacer':
      return { content: {}, styles: { height: '20px' } };
    case 'list':
      return { content: { items: ['Item 1', 'Item 2', 'Item 3'], listType: 'bullet' }, styles: { color: '#334155' } };
    case 'class_details':
      return { content: {}, styles: { boxType: 'info-box' } };
    case 'students_list':
      return { content: {}, styles: {} };
    default:
      return { content: {}, styles: {} };
  }
};

// Sortable Block Component
const SortableBlock = ({ block, index, onUpdate, onDelete, onSelect, isSelected, variables }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Helper to ensure text is readable on dark background
  const getReadableColor = (color) => {
    if (!color) return '#e2e8f0'; // Default light slate
    // If color is too dark, lighten it for preview
    const hex = color.replace('#', '');
    if (hex.length === 6) {
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;
      if (brightness < 128) {
        // Color is dark, lighten it for preview
        return `rgb(${Math.min(255, r + 100)}, ${Math.min(255, g + 100)}, ${Math.min(255, b + 100)})`;
      }
    }
    return color;
  };

  const renderBlockPreview = () => {
    const { type, content, styles } = block;
    
    switch (type) {
      case 'heading':
        const HeadingTag = `h${content.level || 2}`;
        return (
          <div style={{ color: getReadableColor(styles?.color), textAlign: styles?.textAlign }}>
            <HeadingTag className="font-bold">{content.text || 'Heading'}</HeadingTag>
          </div>
        );
      case 'text':
        return (
          <p style={{ color: getReadableColor(styles?.color), textAlign: styles?.textAlign, fontSize: styles?.fontSize }} className="text-slate-200">
            {content.text || 'Text content...'}
          </p>
        );
      case 'button':
        return (
          <div style={{ textAlign: styles?.textAlign }}>
            <span 
              className="inline-block px-4 py-2 rounded-md font-semibold"
              style={{ backgroundColor: styles?.backgroundColor || '#0ea5e9', color: styles?.color || '#ffffff' }}
            >
              {content.text || 'Button'}
            </span>
          </div>
        );
      case 'image':
        return (
          <div style={{ textAlign: styles?.textAlign }} className="text-white">
            <Image className="h-12 w-12 mx-auto opacity-50" />
            <p className="text-xs mt-1">{content.src ? 'Image' : 'No image set'}</p>
          </div>
        );
      case 'divider':
        return <hr style={{ borderColor: styles?.borderColor || '#64748b' }} className="my-2 border-t-2" />;
      case 'spacer':
        return (
          <div style={{ height: styles?.height }} className="bg-slate-700/20 border border-dashed border-teal-600 rounded flex items-center justify-center">
            <span className="text-xs text-white">Spacer ({styles?.height})</span>
          </div>
        );
      case 'list':
        const ListTag = content.listType === 'numbered' ? 'ol' : 'ul';
        return (
          <ListTag className="pl-5 text-slate-200" style={{ listStyleType: content.listType === 'numbered' ? 'decimal' : 'disc' }}>
            {(content.items || []).slice(0, 3).map((item, i) => (
              <li key={i} className="text-sm">{item}</li>
            ))}
            {(content.items || []).length > 3 && <li className="text-white">...</li>}
          </ListTag>
        );
      case 'class_details':
        return (
          <div className="bg-sky-900/30 border-l-4 border-sky-500 p-3 rounded-r text-sm">
            <p className="font-semibold text-sky-300">Class Details Block</p>
            <p className="text-white text-xs">Auto-generated from booking data</p>
          </div>
        );
      case 'students_list':
        return (
          <div className="bg-purple-900/30 border-l-4 border-purple-500 p-3 rounded-r text-sm">
            <p className="font-semibold text-purple-300">Students List Block</p>
            <p className="text-white text-xs">Auto-generated from booking data</p>
          </div>
        );
      default:
        return <div className="text-white">Unknown block type</div>;
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group relative bg-slate-800 rounded-lg border ${isSelected ? 'border-teal-500 ring-2 ring-teal-500/30' : 'border-slate-700'} p-3 mb-2 ${isDragging ? 'shadow-lg' : ''}`}
      onClick={() => onSelect(block.id)}
      data-testid={`email-block-${block.id}`}
    >
      {/* Drag handle and controls */}
      <div className="absolute -left-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 bg-slate-700 rounded text-white hover:text-slate-200"
        >
          <GripVertical className="h-4 w-4" />
        </div>
      </div>
      
      <div className="absolute -right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => { e.stopPropagation(); onDelete(block.id); }}
          className="p-1 h-6 w-6 bg-red-900/50 hover:bg-red-900 text-red-400"
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
      
      {/* Block type badge */}
      <div className="absolute top-1 right-2">
        <Badge variant="outline" className="text-xs bg-slate-700 border-teal-600 text-white">
          {block.type}
        </Badge>
      </div>
      
      {/* Block preview */}
      <div className="mt-4 min-h-[40px]">
        {renderBlockPreview()}
      </div>
    </div>
  );
};

// Block Editor Panel
const BlockEditorPanel = ({ block, onUpdate, variables }) => {
  if (!block) {
    return (
      <div className="h-full flex items-center justify-center text-slate-500">
        <div className="text-center">
          <FileText className="h-12 w-12 mx-auto mb-2 opacity-30" />
          <p>Select a block to edit</p>
        </div>
      </div>
    );
  }

  const { type, content, styles } = block;

  const updateContent = (key, value) => {
    onUpdate(block.id, { ...block, content: { ...content, [key]: value } });
  };

  const updateStyles = (key, value) => {
    onUpdate(block.id, { ...block, styles: { ...styles, [key]: value } });
  };

  const insertVariable = (variable, targetField) => {
    const currentValue = content[targetField] || '';
    updateContent(targetField, currentValue + `{{${variable}}}`);
  };

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-200 capitalize">{type} Settings</h3>
        <Badge className="bg-slate-700">{type}</Badge>
      </div>

      {/* Content Settings based on block type */}
      {type === 'heading' && (
        <>
          <div>
            <Label className="text-white">Heading Text</Label>
            <Textarea
              value={content.text || ''}
              onChange={(e) => updateContent('text', e.target.value)}
              className="bg-slate-700 border-teal-600 text-white mt-1"
              rows={2}
            />
            <VariableButtons variables={variables} onInsert={(v) => insertVariable(v, 'text')} />
          </div>
          <div>
            <Label className="text-white">Heading Level</Label>
            <Select value={String(content.level || 2)} onValueChange={(v) => updateContent('level', parseInt(v))}>
              <SelectTrigger className="bg-slate-700 border-teal-600 text-white mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="1">H1 - Large</SelectItem>
                <SelectItem value="2">H2 - Medium</SelectItem>
                <SelectItem value="3">H3 - Small</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </>
      )}

      {type === 'text' && (
        <div>
          <Label className="text-white">Text Content</Label>
          <Textarea
            value={content.text || ''}
            onChange={(e) => updateContent('text', e.target.value)}
            className="bg-slate-700 border-teal-600 text-white mt-1"
            rows={4}
          />
          <VariableButtons variables={variables} onInsert={(v) => insertVariable(v, 'text')} />
        </div>
      )}

      {type === 'button' && (
        <>
          <div>
            <Label className="text-white">Button Text</Label>
            <Input
              value={content.text || ''}
              onChange={(e) => updateContent('text', e.target.value)}
              className="bg-slate-700 border-teal-600 text-white mt-1"
            />
          </div>
          <div>
            <Label className="text-white">Button URL</Label>
            <Input
              value={content.url || ''}
              onChange={(e) => updateContent('url', e.target.value)}
              className="bg-slate-700 border-teal-600 text-white mt-1"
              placeholder="https://... or {{payment_url}}"
            />
            <VariableButtons variables={variables.filter(v => v.includes('url'))} onInsert={(v) => updateContent('url', `{{${v}}}`)} />
          </div>
          <div>
            <Label className="text-white">Button Color</Label>
            <div className="flex gap-2 mt-1">
              <Input
                type="color"
                value={styles.backgroundColor || '#0ea5e9'}
                onChange={(e) => updateStyles('backgroundColor', e.target.value)}
                className="w-12 h-10 p-1 bg-slate-700 border-teal-600"
              />
              <Input
                value={styles.backgroundColor || '#0ea5e9'}
                onChange={(e) => updateStyles('backgroundColor', e.target.value)}
                className="flex-1 bg-slate-700 border-teal-600 text-white"
              />
            </div>
          </div>
        </>
      )}

      {type === 'image' && (
        <>
          <div>
            <Label className="text-white">Image URL</Label>
            <Input
              value={content.src || ''}
              onChange={(e) => updateContent('src', e.target.value)}
              className="bg-slate-700 border-teal-600 text-white mt-1"
              placeholder="https://..."
            />
          </div>
          <div>
            <Label className="text-white">Alt Text</Label>
            <Input
              value={content.alt || ''}
              onChange={(e) => updateContent('alt', e.target.value)}
              className="bg-slate-700 border-teal-600 text-white mt-1"
            />
          </div>
          <div>
            <Label className="text-white">Width</Label>
            <Select value={styles.width || '100%'} onValueChange={(v) => updateStyles('width', v)}>
              <SelectTrigger className="bg-slate-700 border-teal-600 text-white mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="100%">Full Width</SelectItem>
                <SelectItem value="75%">75%</SelectItem>
                <SelectItem value="50%">50%</SelectItem>
                <SelectItem value="25%">25%</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </>
      )}

      {type === 'spacer' && (
        <div>
          <Label className="text-white">Height</Label>
          <Select value={styles.height || '20px'} onValueChange={(v) => updateStyles('height', v)}>
            <SelectTrigger className="bg-slate-700 border-teal-600 text-white mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              <SelectItem value="10px">Small (10px)</SelectItem>
              <SelectItem value="20px">Medium (20px)</SelectItem>
              <SelectItem value="40px">Large (40px)</SelectItem>
              <SelectItem value="60px">Extra Large (60px)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {type === 'divider' && (
        <div>
          <Label className="text-white">Line Color</Label>
          <div className="flex gap-2 mt-1">
            <Input
              type="color"
              value={styles.borderColor || '#e2e8f0'}
              onChange={(e) => updateStyles('borderColor', e.target.value)}
              className="w-12 h-10 p-1 bg-slate-700 border-teal-600"
            />
            <Input
              value={styles.borderColor || '#e2e8f0'}
              onChange={(e) => updateStyles('borderColor', e.target.value)}
              className="flex-1 bg-slate-700 border-teal-600 text-white"
            />
          </div>
        </div>
      )}

      {type === 'list' && (
        <>
          <div>
            <Label className="text-white">List Type</Label>
            <Select value={content.listType || 'bullet'} onValueChange={(v) => updateContent('listType', v)}>
              <SelectTrigger className="bg-slate-700 border-teal-600 text-white mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="bullet">Bullet Points</SelectItem>
                <SelectItem value="numbered">Numbered</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-white">List Items (one per line)</Label>
            <textarea
              value={(content.items || []).join('\n')}
              onChange={(e) => {
                const lines = e.target.value.split('\n');
                // Keep empty lines while typing (they'll be filtered on save)
                updateContent('items', lines);
              }}
              onBlur={(e) => {
                // Filter out empty lines when user leaves the field
                const lines = e.target.value.split('\n');
                updateContent('items', lines.filter(line => line.trim() !== ''));
              }}
              onKeyDown={(e) => {
                // Allow Enter key to work normally in textarea
                // Stop propagation to prevent dialog/form interference
                if (e.key === 'Enter') {
                  e.stopPropagation();
                  // Don't prevent default - we WANT the newline to be inserted
                }
              }}
              className="w-full bg-slate-700 border border-teal-600 text-white mt-1 rounded-md p-2 min-h-[120px] resize-y focus:outline-none focus:ring-2 focus:ring-teal-500"
              placeholder="Enter each list item on a new line..."
              rows={6}
            />
            <p className="text-xs text-slate-500 mt-1">Press Enter to add a new line</p>
          </div>
        </>
      )}

      {type === 'class_details' && (
        <div>
          <Label className="text-white">Box Style</Label>
          <Select value={styles.boxType || 'info-box'} onValueChange={(v) => updateStyles('boxType', v)}>
            <SelectTrigger className="bg-slate-700 border-teal-600 text-white mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              <SelectItem value="info-box">Info (Blue)</SelectItem>
              <SelectItem value="success-box">Success (Green)</SelectItem>
              <SelectItem value="warning-box">Warning (Yellow)</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-slate-500 mt-2">
            This block auto-generates class details including name, date, time, location, address, and amount paid.
          </p>
        </div>
      )}

      {type === 'students_list' && (
        <p className="text-sm text-white">
          This block auto-generates a list of all students with their contact info, add-ons, and notes.
        </p>
      )}

      {/* Common Style Settings */}
      {['heading', 'text', 'button', 'image'].includes(type) && (
        <div className="pt-4 border-t border-slate-700">
          <Label className="text-white mb-2 block">Alignment</Label>
          <div className="flex gap-1">
            {['left', 'center', 'right'].map((align) => (
              <Button
                key={align}
                variant={styles.textAlign === align ? 'default' : 'outline'}
                size="sm"
                onClick={() => updateStyles('textAlign', align)}
                className={styles.textAlign === align ? 'bg-teal-600' : 'bg-slate-700 border-teal-600'}
              >
                {align === 'left' && <AlignLeft className="h-4 w-4" />}
                {align === 'center' && <AlignCenter className="h-4 w-4" />}
                {align === 'right' && <AlignRight className="h-4 w-4" />}
              </Button>
            ))}
          </div>
        </div>
      )}

      {['heading', 'text'].includes(type) && (
        <div>
          <Label className="text-white">Text Color</Label>
          <div className="flex gap-2 mt-1">
            <Input
              type="color"
              value={styles.color || '#334155'}
              onChange={(e) => updateStyles('color', e.target.value)}
              className="w-12 h-10 p-1 bg-slate-700 border-teal-600"
            />
            <Input
              value={styles.color || '#334155'}
              onChange={(e) => updateStyles('color', e.target.value)}
              className="flex-1 bg-slate-700 border-teal-600 text-white"
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Variable insertion buttons
const VariableButtons = ({ variables, onInsert }) => {
  if (!variables || variables.length === 0) return null;
  
  return (
    <div className="mt-2">
      <p className="text-xs text-slate-500 mb-1">Insert variable:</p>
      <div className="flex flex-wrap gap-1">
        {variables.map((v) => (
          <Button
            key={v}
            variant="outline"
            size="sm"
            onClick={() => onInsert(v)}
            className="text-xs h-6 px-2 bg-slate-700 border-teal-600 text-teal-400 hover:bg-slate-600"
          >
            {`{{${v}}}`}
          </Button>
        ))}
      </div>
    </div>
  );
};

// Main Email Template Editor Component
const EmailTemplateEditor = ({ isOpen, onClose }) => {
  const [templateTypes, setTemplateTypes] = useState({});
  const [selectedType, setSelectedType] = useState(null);
  const [template, setTemplate] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [subject, setSubject] = useState('');
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [isActive, setIsActive] = useState(true);
  const [previewHtml, setPreviewHtml] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [blockTemplates, setBlockTemplates] = useState([]);
  const [showBlockTemplateManager, setShowBlockTemplateManager] = useState(false);
  const [newBlockTemplate, setNewBlockTemplate] = useState({ name: '', description: '', category: 'custom' });
  const [savingBlockTemplate, setSavingBlockTemplate] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Fetch template types on mount
  useEffect(() => {
    if (isOpen) {
      fetchTemplateTypes();
      fetchBlockTemplates();
    }
  }, [isOpen]);

  const fetchTemplateTypes = async () => {
    try {
      const res = await axios.get(`${API}/admin/email-templates/types`);
      setTemplateTypes(res.data);
    } catch (err) {
      toast.error('Failed to load template types');
    }
  };

  const fetchBlockTemplates = async () => {
    try {
      const res = await axios.get(`${API}/admin/block-templates`);
      setBlockTemplates(res.data);
    } catch (err) {
      console.error('Failed to load block templates');
    }
  };

  const insertBlockTemplate = (blockTemplate) => {
    // Clone all blocks from the template with new IDs
    const newBlocks = blockTemplate.blocks.map((block, idx) => ({
      ...block,
      id: `block-${Date.now()}-${idx}`,
    }));
    setBlocks([...blocks, ...newBlocks]);
    toast.success(`Added "${blockTemplate.name}" blocks`);
  };

  const saveCurrentAsBlockTemplate = async () => {
    if (!newBlockTemplate.name.trim()) {
      toast.error('Please enter a template name');
      return;
    }
    if (blocks.length === 0) {
      toast.error('No blocks to save as template');
      return;
    }
    
    setSavingBlockTemplate(true);
    try {
      await axios.post(`${API}/admin/block-templates`, {
        name: newBlockTemplate.name,
        description: newBlockTemplate.description,
        category: newBlockTemplate.category,
        blocks: blocks,
      });
      toast.success('Block template saved!');
      setNewBlockTemplate({ name: '', description: '', category: 'custom' });
      setShowBlockTemplateManager(false);
      fetchBlockTemplates();
    } catch (err) {
      toast.error('Failed to save block template');
    }
    setSavingBlockTemplate(false);
  };

  const deleteBlockTemplate = async (templateId) => {
    if (!window.confirm('Delete this block template?')) return;
    try {
      await axios.delete(`${API}/admin/block-templates/${templateId}`);
      toast.success('Block template deleted');
      fetchBlockTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete template');
    }
  };

  const loadTemplate = async (type) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/email-templates/${type}`);
      setTemplate(res.data);
      setBlocks(res.data.blocks || []);
      setSubject(res.data.subject || '');
      setIsActive(res.data.is_active !== false);
      setSelectedBlockId(null);
    } catch (err) {
      // Template doesn't exist yet, start fresh
      setTemplate({ template_type: type, is_default: true });
      setBlocks([]);
      setSubject('');
      setIsActive(true);
    }
    setLoading(false);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setBlocks((items) => {
        const oldIndex = items.findIndex((i) => i.id === active.id);
        const newIndex = items.findIndex((i) => i.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const addBlock = (type) => {
    const newBlock = {
      id: `block-${Date.now()}`,
      type,
      ...getDefaultBlockContent(type),
    };
    setBlocks([...blocks, newBlock]);
    setSelectedBlockId(newBlock.id);
  };

  const updateBlock = (id, updatedBlock) => {
    setBlocks(blocks.map((b) => (b.id === id ? updatedBlock : b)));
  };

  const deleteBlock = (id) => {
    setBlocks(blocks.filter((b) => b.id !== id));
    if (selectedBlockId === id) setSelectedBlockId(null);
  };

  const generatePreview = async () => {
    try {
      const res = await axios.post(`${API}/admin/email-templates/${selectedType}/preview`, {
        blocks,
        subject,
      });
      setPreviewHtml(res.data.html);
      setShowPreview(true);
    } catch (err) {
      toast.error('Failed to generate preview');
    }
  };

  const saveTemplate = async () => {
    if (!selectedType) return;
    setSaving(true);
    try {
      await axios.put(`${API}/admin/email-templates/${selectedType}`, {
        name: templateTypes[selectedType]?.name || selectedType,
        subject,
        blocks,
        is_active: isActive,
      });
      toast.success('Template saved successfully');
    } catch (err) {
      toast.error('Failed to save template');
    }
    setSaving(false);
  };

  const sendTestEmail = async () => {
    if (!testEmail || !selectedType) return;
    setSendingTest(true);
    try {
      await axios.post(`${API}/admin/email-templates/${selectedType}/test?to_email=${encodeURIComponent(testEmail)}`);
      toast.success(`Test email sent to ${testEmail}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send test email');
    }
    setSendingTest(false);
  };

  const resetToDefault = async () => {
    if (!selectedType) return;
    try {
      // Use the new reset endpoint to restore default template content
      await axios.post(`${API}/admin/email-templates/${selectedType}/reset`);
      await loadTemplate(selectedType);
      toast.success('Template reset to default');
    } catch (err) {
      // Fallback to delete if reset fails (for older templates without defaults)
      try {
        await axios.delete(`${API}/admin/email-templates/${selectedType}`);
        await loadTemplate(selectedType);
        toast.success('Reset to default template');
      } catch (deleteErr) {
        toast.error('Failed to reset template');
      }
    }
  };

  const selectedBlock = blocks.find((b) => b.id === selectedBlockId);
  const currentVariables = selectedType ? (templateTypes[selectedType]?.variables || []) : [];
  const customerTemplates = Object.entries(templateTypes).filter(([_, t]) => !t.is_admin);
  const adminTemplates = Object.entries(templateTypes).filter(([_, t]) => t.is_admin);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 text-white border-slate-700 p-0 flex flex-col" style={{ width: '95vw', maxWidth: '1600px', height: '90vh', maxHeight: '900px' }}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700 shrink-0">
          <div className="flex items-center gap-3">
            <Mail className="h-6 w-6 text-teal-400" />
            <div>
              <DialogTitle className="text-lg font-bold text-slate-100">Email Template Editor</DialogTitle>
              <DialogDescription className="text-white">
                Customize your email templates with drag-and-drop blocks
              </DialogDescription>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-white hover:text-slate-200">
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="flex flex-1 overflow-hidden min-h-0">
            {/* Template List Sidebar */}
            <div className="w-56 border-r border-slate-700 overflow-y-auto p-3 shrink-0">
              <h3 className="font-semibold text-white mb-2 text-sm">Customer Emails</h3>
              <div className="space-y-1 mb-4">
                {customerTemplates.map(([type, info]) => (
                  <button
                    key={type}
                    onClick={() => { setSelectedType(type); loadTemplate(type); }}
                    className={`w-full text-left px-2 py-1.5 rounded-lg text-xs transition-colors ${
                      selectedType === type 
                        ? 'bg-teal-600/20 text-teal-400 border border-teal-500/50' 
                        : 'text-white hover:bg-slate-800'
                    }`}
                  >
                    {info.name}
                  </button>
                ))}
              </div>
              
              <h3 className="font-semibold text-white mb-2 text-sm">Admin Notifications</h3>
              <div className="space-y-1">
                {adminTemplates.map(([type, info]) => (
                  <button
                    key={type}
                    onClick={() => { setSelectedType(type); loadTemplate(type); }}
                    className={`w-full text-left px-2 py-1.5 rounded-lg text-xs transition-colors ${
                      selectedType === type 
                        ? 'bg-purple-600/20 text-purple-400 border border-purple-500/50' 
                        : 'text-white hover:bg-slate-800'
                    }`}
                  >
                    {info.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Main Editor Area */}
            {selectedType ? (
              <div className="flex-1 flex flex-col overflow-hidden min-w-0">
                <div className="flex flex-1 overflow-hidden min-h-0">
                  {/* Blocks Panel */}
                  <div className="w-44 border-r border-slate-700 p-2 overflow-y-auto shrink-0">
                    <h4 className="font-semibold text-white text-xs uppercase mb-2">Add Block</h4>
                    <div className="space-y-1">
                      {BLOCK_TYPES.map(({ type, label, icon: Icon, description }) => (
                        <button
                          key={type}
                          onClick={() => addBlock(type)}
                          className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-xs transition-colors group"
                          title={description}
                        >
                          <Icon className="h-3.5 w-3.5 text-slate-500 group-hover:text-teal-400 shrink-0" />
                          {label}
                        </button>
                      ))}
                    </div>
                    
                    {/* Block Templates Section */}
                    <div className="mt-4 pt-3 border-t border-slate-700">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold text-white text-xs uppercase">Block Templates</h4>
                        <button 
                          onClick={() => setShowBlockTemplateManager(true)}
                          className="text-teal-400 hover:text-teal-300"
                          title="Manage block templates"
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <div className="space-y-1">
                        {blockTemplates.map((bt) => (
                          <button
                            key={bt.id}
                            onClick={() => insertBlockTemplate(bt)}
                            className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg bg-purple-900/30 hover:bg-purple-800/40 text-white text-xs transition-colors group border border-purple-700/50"
                            title={bt.description || `Insert ${bt.name} blocks`}
                          >
                            <Copy className="h-3.5 w-3.5 text-purple-400 group-hover:text-purple-300 shrink-0" />
                            <span className="truncate">{bt.name}</span>
                          </button>
                        ))}
                        {blockTemplates.length === 0 && (
                          <p className="text-xs text-slate-500 italic">No templates yet</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Canvas - Main editing area */}
                  <div className="flex-1 flex flex-col overflow-hidden min-h-0 min-w-0">
                    {/* Subject Line */}
                    <div className="p-3 border-b border-slate-700 shrink-0">
                      <Label className="text-white mb-1 block text-sm">Email Subject</Label>
                      <Input
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        placeholder="Enter email subject..."
                        className="bg-slate-800 border-teal-600 text-white"
                      />
                      <div className="mt-2">
                        <p className="text-xs text-slate-500 mb-1">Insert variable:</p>
                        <div className="flex flex-wrap gap-1">
                          {['class_name', 'class_date', 'to_name'].map((v) => (
                            <Button
                              key={v}
                              variant="outline"
                              size="sm"
                              onClick={() => setSubject(subject + `{{${v}}}`)}
                              className="text-xs h-5 px-1.5 bg-slate-700 border-teal-600 text-teal-400"
                            >
                              {`{{${v}}}`}
                            </Button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Blocks Canvas - Scrollable */}
                    <div className="flex-1 overflow-y-auto p-3 bg-slate-950/50 min-h-0">
                    {loading ? (
                      <div className="flex items-center justify-center h-full">
                        <Loader2 className="h-8 w-8 animate-spin text-teal-400" />
                      </div>
                    ) : blocks.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-full text-slate-500">
                        <FileText className="h-12 w-12 mb-3 opacity-30" />
                        <p className="text-base">No blocks added yet</p>
                        <p className="text-xs">Click a block type from the left panel to add it</p>
                      </div>
                    ) : (
                      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <SortableContext items={blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
                          {blocks.map((block, index) => (
                            <SortableBlock
                              key={block.id}
                              block={block}
                              index={index}
                              onUpdate={updateBlock}
                              onDelete={deleteBlock}
                              onSelect={setSelectedBlockId}
                              isSelected={selectedBlockId === block.id}
                              variables={currentVariables}
                            />
                          ))}
                        </SortableContext>
                      </DndContext>
                    )}
                  </div>
                </div>

                {/* Block Editor Panel - Right side */}
                <div className="w-80 border-l border-slate-700 overflow-y-auto bg-slate-800/50 shrink-0">
                  <BlockEditorPanel
                    block={selectedBlock}
                    onUpdate={updateBlock}
                    variables={currentVariables}
                  />
                </div>
              </div>

              {/* Actions Bar - Fixed at bottom, always visible */}
              <div className="p-3 border-t-2 border-teal-600 bg-slate-800 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={isActive}
                      onCheckedChange={setIsActive}
                      className="data-[state=checked]:bg-teal-500"
                    />
                    <Label className="text-white text-sm">Use custom template</Label>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={resetToDefault}
                    className="text-white border-teal-600 hover:bg-slate-700"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Reset
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    onClick={generatePreview} 
                    className="border-blue-500 text-white hover:bg-blue-900/30 hover:text-blue-300"
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Preview & Test
                  </Button>
                  <Button onClick={saveTemplate} disabled={saving} className="bg-teal-600 hover:bg-teal-500 text-white font-semibold">
                    {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    Save Template
                  </Button>
                </div>
              </div>
            </div>
          ) : (
              <div className="flex-1 flex items-center justify-center text-slate-500">
                <div className="text-center">
                  <Mail className="h-16 w-16 mx-auto mb-4 opacity-30" />
                  <p className="text-lg">Select a template to edit</p>
                  <p className="text-sm">Choose from the list on the left</p>
                </div>
              </div>
            )}
        </div>

        {/* Preview Modal */}
        <Dialog open={showPreview} onOpenChange={setShowPreview}>
          <DialogContent className="bg-slate-900 text-white border-slate-700 max-w-4xl max-h-[90vh] overflow-hidden">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-xl">
                <Eye className="h-6 w-6 text-teal-400" />
                Email Preview & Test
              </DialogTitle>
              <DialogDescription className="text-white">
                Preview how your email will look and send a test to verify
              </DialogDescription>
            </DialogHeader>
            
            {/* Test Email Section - Prominent at top */}
            <div className="bg-slate-800 rounded-lg p-4 border border-teal-600">
              <Label className="text-teal-400 font-semibold mb-2 block">Send Test Email</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="your-email@example.com"
                  className="bg-slate-700 border-teal-600 text-white flex-1 text-lg"
                />
                <Button
                  onClick={sendTestEmail}
                  disabled={sendingTest || !testEmail}
                  size="lg"
                  className="bg-teal-600 hover:bg-teal-500 text-white font-semibold px-6"
                >
                  {sendingTest ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <Send className="h-5 w-5 mr-2" />}
                  Send Test Email
                </Button>
              </div>
              <p className="text-slate-500 text-xs mt-2">Test email will be sent with sample data to preview formatting</p>
            </div>
            
            {/* Email Preview */}
            <div className="mt-4">
              <Label className="text-white font-semibold mb-2 block">Email Preview</Label>
              <div className="bg-white rounded-lg overflow-auto max-h-[50vh] border border-teal-600">
                <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
              </div>
            </div>
            
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setShowPreview(false)} className="border-teal-600 text-white">
                Close Preview
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Block Template Manager Dialog */}
        <Dialog open={showBlockTemplateManager} onOpenChange={setShowBlockTemplateManager}>
          <DialogContent className="max-w-2xl bg-slate-900 border-slate-700 text-slate-100">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Copy className="h-5 w-5 text-purple-400" />
                Block Templates Manager
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                Create reusable block templates that can be inserted into any email template.
              </DialogDescription>
            </DialogHeader>
            
            {/* Create New Template */}
            <div className="bg-slate-800 rounded-lg p-4 border border-purple-600/50">
              <h4 className="text-purple-400 font-semibold mb-3">Save Current Blocks as Template</h4>
              <div className="space-y-3">
                <div>
                  <Label className="text-white text-sm">Template Name</Label>
                  <Input
                    value={newBlockTemplate.name}
                    onChange={(e) => setNewBlockTemplate({ ...newBlockTemplate, name: e.target.value })}
                    placeholder="e.g., AHA Ebook Links"
                    className="bg-slate-700 border-slate-600 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-white text-sm">Description (optional)</Label>
                  <Input
                    value={newBlockTemplate.description}
                    onChange={(e) => setNewBlockTemplate({ ...newBlockTemplate, description: e.target.value })}
                    placeholder="Brief description..."
                    className="bg-slate-700 border-slate-600 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-white text-sm">Category</Label>
                  <Select 
                    value={newBlockTemplate.category} 
                    onValueChange={(v) => setNewBlockTemplate({ ...newBlockTemplate, category: v })}
                  >
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="custom">Custom</SelectItem>
                      <SelectItem value="resources">Resources</SelectItem>
                      <SelectItem value="info">Information</SelectItem>
                      <SelectItem value="cta">Call to Action</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={saveCurrentAsBlockTemplate}
                  disabled={savingBlockTemplate || !newBlockTemplate.name.trim()}
                  className="w-full bg-purple-600 hover:bg-purple-500"
                >
                  {savingBlockTemplate ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Save Current Blocks ({blocks.length} blocks)
                </Button>
              </div>
            </div>
            
            {/* Existing Templates */}
            <div className="mt-4">
              <h4 className="text-white font-semibold mb-3">Existing Block Templates</h4>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {blockTemplates.map((bt) => (
                  <div key={bt.id} className="flex items-center justify-between bg-slate-800 rounded-lg p-3 border border-slate-700">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium">{bt.name}</span>
                        {bt.is_default && <Badge variant="outline" className="text-xs border-purple-500 text-purple-400">Default</Badge>}
                        <Badge variant="outline" className="text-xs border-slate-600 text-slate-400">{bt.blocks?.length || 0} blocks</Badge>
                      </div>
                      {bt.description && <p className="text-xs text-slate-500 mt-1">{bt.description}</p>}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { insertBlockTemplate(bt); setShowBlockTemplateManager(false); }}
                        className="border-purple-600 text-purple-400 hover:bg-purple-600/20"
                      >
                        <Plus className="h-3 w-3 mr-1" /> Insert
                      </Button>
                      {!bt.is_default && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deleteBlockTemplate(bt.id)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
                {blockTemplates.length === 0 && (
                  <p className="text-slate-500 text-center py-4">No block templates yet. Create one above!</p>
                )}
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowBlockTemplateManager(false)} className="border-slate-600 text-white">
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};

export default EmailTemplateEditor;
